import { parseISO } from "date-fns";
import { useMemo } from "react";

import type { MenuData } from "@luna-restaurant/feature-luna-school-menu-plan/build/components/MenuCard/types";
import type { DishPlanItem } from "@luna-restaurant/school-api";

import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  DISH_PLAN_STALE_TIME_MS,
  type NormalizedPlanItem,
  formatDateKey,
  groupItemsToMenuData,
  normalizePlanItem,
} from "@/utils/dishPlanHelpers";

type Params = {
  dateFrom: string;
  dateTo: string;
  enabled: boolean;
};

type MealPlanDay = MenuData & { dateKey: string };

type Result = {
  mealPlan: MealPlanDay[] | null;
  isLoading: boolean;
  isError: boolean;
};

export const useGuestOrderMealPlan = ({
  dateFrom,
  dateTo,
  enabled,
}: Params): Result => {
  const { currentUser } = useCurrentUser();
  const schoolPath = currentUser?.school;

  const displayStart = useMemo(() => {
    if (!dateFrom) return null;
    const parsed = parseISO(dateFrom);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [dateFrom]);

  const displayEnd = useMemo(() => {
    if (!dateTo) return null;
    const parsed = parseISO(dateTo);
    return isNaN(parsed.getTime()) ? null : parsed;
  }, [dateTo]);

  const schoolItemsQuery = useCollection<DishPlanItem>({
    queryKey: ["school-dish-plan-items", dateFrom, dateTo],
    path: schoolPath ? `${schoolPath}/dish-plan-items` : "",
    enabled: enabled && !!schoolPath,
    query: {
      "date[after]": dateFrom,
      "date[before]": dateTo,
    },
    staleTime: DISH_PLAN_STALE_TIME_MS,
  });

  const isLoading = schoolItemsQuery.isLoading;
  const isError = schoolItemsQuery.isError;

  const mealPlan = useMemo(() => {
    if (!enabled || !displayStart || !displayEnd) return null;
    const items = schoolItemsQuery.items;
    if (!items || items.length === 0) return null;

    const normalized = items
      .map((item) => normalizePlanItem(item, displayStart, displayEnd))
      .filter((item): item is NormalizedPlanItem => item !== null);

    if (normalized.length === 0) return null;

    const byDate = new Map<string, NormalizedPlanItem[]>();
    for (const item of normalized) {
      const key = formatDateKey(item.date);
      const group = byDate.get(key);
      if (group) {
        group.push(item);
      } else {
        byDate.set(key, [item]);
      }
    }

    const result: { dateKey: string; menuData: MenuData }[] = [];
    for (const [dateKey, dateItems] of byDate.entries()) {
      const menuData = groupItemsToMenuData(dateItems);
      if (menuData) result.push({ dateKey, menuData });
    }

    result.sort((a, b) => a.dateKey.localeCompare(b.dateKey));

    const sorted = result.map((r) => ({ ...r.menuData, dateKey: r.dateKey }));
    return sorted.length > 0 ? sorted : null;
  }, [enabled, displayStart, displayEnd, schoolItemsQuery.items]);

  return { mealPlan, isLoading, isError };
};
